import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CollapseAllIcon,
  ExpandAllIcon,
  FolderIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '../../../components/ui';
import { ListRefreshHint } from '../../../components/generic/ListRefreshHint';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  getCoaTreeCollapseStorageKey,
  loadPersistedTreeCollapsedIds,
  persistTreeCollapsedIds,
} from '../../../lib/listStatePersistence';
import { fetcher } from '../../../services/fetcher';
import { buildListQuery, normalizePaginatedListResponse, type PaginatedApiResponse } from '../../../services/paginatedList';
import { moduleConfig as categoriesModuleConfig } from '../spare-categories/config';
import {
  buildSpareCategoryTree,
  useSpareCategoryTree,
  type SpareCategory,
  type SpareCategoryTreeNode,
} from '../spare-categories/useData';
import { moduleConfig } from './config';
import { SpareCategoryFormPanel } from './SpareCategoryFormPanel';
import { SpareItemsTable } from './SpareItemsTable';
import {
  collectCollapsedIdsBelowRoot,
  collectExpandableCategoryIds,
  countDirectItemsInCategory,
  filterCategoryTree,
  filterItemsForCategory,
  findCategoryNodeById,
  flattenCategoryTree,
  getCategoryPath,
  type CategoryTreeRow,
} from './treeListUtils';
import type { SpareItem } from './useData';

type Library = { id: number; name: string };

const PANEL_WIDTH_KEY = `spare-items-panel-width:${moduleConfig.module.route.list}`;
const INCLUDE_SUBCATS_KEY = `spare-items-include-subcats:${moduleConfig.module.route.list}`;
const PANEL_MIN = 260;
const PANEL_MAX = 560;
const PANEL_DEFAULT = 384;

const selectedCategoryStorageKey = (libraryId: number) =>
  `spare-items-selected-category:${moduleConfig.module.route.list}:${libraryId}`;

const selectedLibraryStorageKey = () =>
  `spare-items-selected-library:${moduleConfig.module.route.list}`;

const loadPanelWidth = (): number => {
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    const n = raw != null ? Number(raw) : PANEL_DEFAULT;
    if (!Number.isFinite(n)) return PANEL_DEFAULT;
    return Math.min(PANEL_MAX, Math.max(PANEL_MIN, n));
  } catch {
    return PANEL_DEFAULT;
  }
};

const loadIncludeSubcats = (): boolean => {
  try {
    const raw = localStorage.getItem(INCLUDE_SUBCATS_KEY);
    if (raw === null) return true;
    return raw === '1';
  } catch {
    return true;
  }
};

const loadSelectedLibraryId = (): number | null => {
  try {
    const raw = sessionStorage.getItem(selectedLibraryStorageKey());
    if (raw == null || raw === '') return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

const loadSelectedCategoryId = (libraryId: number | null): number | null => {
  if (libraryId == null) return null;
  try {
    const raw = sessionStorage.getItem(selectedCategoryStorageKey(libraryId));
    if (raw == null || raw === '') return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

const resolveInitialCollapsedIds = (libraryId: number, tree: SpareCategoryTreeNode[]): Set<number> => {
  const storageKey = getCoaTreeCollapseStorageKey(moduleConfig.module.route.list, libraryId);
  const fromSession = loadPersistedTreeCollapsedIds(storageKey);
  if (fromSession) return fromSession;
  return new Set(collectCollapsedIdsBelowRoot(tree));
};

const List: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { can } = useAuth();

  const canDelete = can(moduleConfig.module.permissionModuleKey ?? 'spare_items', 'delete');
  const canEdit = can(moduleConfig.module.permissionModuleKey ?? 'spare_items', 'edit');
  const canEditFolders = can(categoriesModuleConfig.module.permissionModuleKey ?? 'spare_categories', 'edit');
  const canDeleteFolders = can(categoriesModuleConfig.module.permissionModuleKey ?? 'spare_categories', 'delete');

  const { data: librariesRaw } = useSWR<Library[] | { data: Library[] }>(
    '/spare-libraries?page=1&limit=500',
    fetcher,
  );
  const libraries = useMemo(
    () => (Array.isArray(librariesRaw) ? librariesRaw : (librariesRaw?.data ?? [])),
    [librariesRaw],
  );

  const [libraryId, setLibraryId] = useState<number | null>(() => loadSelectedLibraryId());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeSubcats, setIncludeSubcats] = useState(loadIncludeSubcats);
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(() =>
    loadSelectedCategoryId(loadSelectedLibraryId()),
  );
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());
  const [treeCollapseReadyForLibrary, setTreeCollapseReadyForLibrary] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState<CategoryTreeRow | null>(null);
  const [folderFormOpen, setFolderFormOpen] = useState(false);
  const [folderFormParentId, setFolderFormParentId] = useState<number | null>(null);
  const [folderFormEditItem, setFolderFormEditItem] = useState<SpareCategoryTreeNode | SpareCategory | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const resizingRef = useRef(false);
  const panelWidthRef = useRef(panelWidth);
  const splitRef = useRef<HTMLDivElement>(null);
  const [panelResizing, setPanelResizing] = useState(false);

  useEffect(() => {
    if (libraryId != null) return;
    if (libraries.length === 0) return;
    setLibraryId(libraries[0].id);
  }, [libraries, libraryId]);

  const { data: flatCategories = [], isLoading: catsLoading, mutate: mutateCats } =
    useSpareCategoryTree(libraryId);

  const categoryTree = useMemo(() => buildSpareCategoryTree(flatCategories), [flatCategories]);
  const filteredCategoryTree = useMemo(
    () => filterCategoryTree(categoryTree, categoryFilter),
    [categoryTree, categoryFilter],
  );
  const isCategoryFilterActive = categoryFilter.trim().length > 0;

  const itemsQuery = useMemo(() => {
    if (libraryId == null) return null;
    const q = buildListQuery(undefined, 1, 2000, {
      filters: { filters: { libraryId: [String(libraryId)] } },
    });
    return `${moduleConfig.module.api.list}${q}`;
  }, [libraryId]);

  const {
    data: itemsRaw,
    isLoading: itemsLoading,
    mutate: mutateItems,
  } = useSWR<PaginatedApiResponse<SpareItem> | SpareItem[]>(itemsQuery, fetcher, {
    revalidateOnFocus: true,
  });

  const libraryItems = useMemo(() => {
    const normalized = normalizePaginatedListResponse<SpareItem>(itemsRaw, 1, 2000);
    return normalized.data;
  }, [itemsRaw]);

  useEffect(() => {
    if (libraryId == null) return;
    setTreeCollapseReadyForLibrary(null);
    setCollapsedIds(new Set());
    setSelectedCategoryId(loadSelectedCategoryId(libraryId));
  }, [libraryId]);

  useEffect(() => {
    if (libraryId == null || catsLoading || categoryTree.length === 0) return;
    if (treeCollapseReadyForLibrary === libraryId) return;
    setCollapsedIds(resolveInitialCollapsedIds(libraryId, categoryTree));
    setTreeCollapseReadyForLibrary(libraryId);
  }, [libraryId, catsLoading, categoryTree, treeCollapseReadyForLibrary]);

  useEffect(() => {
    if (libraryId == null) return;
    const storageKey = getCoaTreeCollapseStorageKey(moduleConfig.module.route.list, libraryId);
    persistTreeCollapsedIds(storageKey, collapsedIds);
  }, [libraryId, collapsedIds]);

  useEffect(() => {
    try {
      if (libraryId == null) sessionStorage.removeItem(selectedLibraryStorageKey());
      else sessionStorage.setItem(selectedLibraryStorageKey(), String(libraryId));
    } catch {
      /* ignore */
    }
  }, [libraryId]);

  useEffect(() => {
    if (libraryId == null) return;
    try {
      if (selectedCategoryId == null) sessionStorage.removeItem(selectedCategoryStorageKey(libraryId));
      else sessionStorage.setItem(selectedCategoryStorageKey(libraryId), String(selectedCategoryId));
    } catch {
      /* ignore */
    }
  }, [libraryId, selectedCategoryId]);

  useEffect(() => {
    try {
      localStorage.setItem(INCLUDE_SUBCATS_KEY, includeSubcats ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [includeSubcats]);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    if (categoryTree.length === 0) {
      if (selectedCategoryId != null) setSelectedCategoryId(null);
      return;
    }
    if (selectedCategoryId != null && findCategoryNodeById(categoryTree, selectedCategoryId)) return;
    setSelectedCategoryId(categoryTree[0]?.id ?? null);
  }, [categoryTree, selectedCategoryId]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizingRef.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const next = Math.min(PANEL_MAX, Math.max(PANEL_MIN, e.clientX - rect.left));
      setPanelWidth(next);
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      setPanelResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidthRef.current));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const expandableIds = useMemo(() => collectExpandableCategoryIds(categoryTree), [categoryTree]);

  const categoryRows = useMemo(() => {
    const collapsed = isCategoryFilterActive ? new Set<number>() : collapsedIds;
    return flattenCategoryTree(filteredCategoryTree, collapsed);
  }, [filteredCategoryTree, collapsedIds, isCategoryFilterActive]);

  const selectedCategory = useMemo(
    () => (selectedCategoryId != null ? findCategoryNodeById(categoryTree, selectedCategoryId) : null),
    [categoryTree, selectedCategoryId],
  );

  const breadcrumb = useMemo(
    () => (selectedCategoryId != null ? getCategoryPath(categoryTree, selectedCategoryId) : []),
    [categoryTree, selectedCategoryId],
  );

  const linkedItems = useMemo(
    () => filterItemsForCategory(libraryItems, selectedCategory, includeSubcats, searchTerm),
    [libraryItems, selectedCategory, includeSubcats, searchTerm],
  );

  const isLoading = catsLoading || itemsLoading;

  const toggleCollapsed = useCallback((id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  const collapseAll = useCallback(() => setCollapsedIds(new Set(expandableIds)), [expandableIds]);

  const selectCategory = useCallback((id: number) => {
    setSelectedCategoryId(id);
    setSearchTerm('');
    setFolderFormOpen(false);
  }, []);

  const startResize = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    resizingRef.current = true;
    setPanelResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const openCreateFolder = useCallback((parentId: number | null) => {
    if (parentId != null) setSelectedCategoryId(parentId);
    setFolderFormParentId(parentId);
    setFolderFormEditItem(null);
    setFolderFormOpen(true);
  }, []);

  const openEditFolder = useCallback((row: SpareCategoryTreeNode) => {
    setSelectedCategoryId(row.id);
    setFolderFormParentId(row.pid ?? null);
    setFolderFormEditItem(row);
    setFolderFormOpen(true);
  }, []);

  const closeFolderForm = useCallback(() => setFolderFormOpen(false), []);

  const categoryCanDelete = useCallback(
    (row: CategoryTreeRow) => {
      if (row.hasChildren) return false;
      return !libraryItems.some((item) => item.categoryId === row.id);
    },
    [libraryItems],
  );

  const folderFormParentLabel = useMemo(() => {
    const pid = folderFormEditItem?.pid ?? folderFormParentId;
    if (pid == null) return null;
    return findCategoryNodeById(categoryTree, pid)?.name ?? null;
  }, [categoryTree, folderFormEditItem, folderFormParentId]);

  const handleDelete = async (id: number) => {
    try {
      await moduleConfig.delete.handler(id);
      await mutateItems();
      setDeleteConfirmId(null);
      showSuccess('Spare item deleted');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || moduleConfig.delete.errorMessage);
    }
  };

  const handleDeleteFolder = async (id: number) => {
    try {
      await categoriesModuleConfig.delete.handler(id);
      await mutateCats();
      if (selectedCategoryId === id) setSelectedCategoryId(null);
      setDeleteFolderConfirm(null);
      setFolderFormOpen(false);
      showSuccess('Folder deleted');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || categoriesModuleConfig.delete.errorMessage);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([mutateCats(), mutateItems()]);
      setLastRefreshedAt(new Date());
    } finally {
      setIsRefreshing(false);
    }
  };

  const newItemHref = useMemo(() => {
    const params = new URLSearchParams();
    if (libraryId != null) params.set('libraryId', String(libraryId));
    if (selectedCategoryId != null) params.set('categoryId', String(selectedCategoryId));
    const qs = params.toString();
    return qs ? `${moduleConfig.module.route.new}?${qs}` : moduleConfig.module.route.new;
  }, [libraryId, selectedCategoryId]);

  return (
    <div className="flex flex-col h-full min-h-0 p-1">
      <div
        ref={splitRef}
        className={cn(
          'flex flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <aside
          className={cn('shrink-0 flex flex-col min-h-0 border-r', colors.border)}
          style={{ width: panelWidth }}
        >
          <div className={cn('px-3 py-2.5 border-b space-y-2', colors.border, colors.surfaceHover)}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
                  Catalog
                </p>
                <p className={cn('truncate text-[11px]', colors.textMuted)}>
                  {libraries.find((l) => l.id === libraryId)?.name ?? 'Select library'}
                </p>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                {canEditFolders && libraryId != null && (
                  <button
                    type="button"
                    onClick={() => openCreateFolder(null)}
                    className={cn(
                      'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                      colors.primaryFg,
                      colors.interactiveHover,
                    )}
                    title="New folder"
                    aria-label="New folder"
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={expandAll}
                  disabled={isCategoryFilterActive || expandableIds.length === 0}
                  className={cn(
                    'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                    colors.textMuted,
                    colors.interactiveHover,
                    'disabled:opacity-40 disabled:pointer-events-none',
                  )}
                  title="Expand all"
                  aria-label="Expand all"
                >
                  <ExpandAllIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={collapseAll}
                  disabled={isCategoryFilterActive || expandableIds.length === 0}
                  className={cn(
                    'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                    colors.textMuted,
                    colors.interactiveHover,
                    'disabled:opacity-40 disabled:pointer-events-none',
                  )}
                  title="Collapse all"
                  aria-label="Collapse all"
                >
                  <CollapseAllIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <select
              className={cn(
                'w-full h-8 rounded-md border px-2 text-sm',
                colors.border,
                colors.surface,
                colors.text,
                'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
              )}
              value={libraryId ?? ''}
              onChange={(e) => {
                const next = e.target.value === '' ? null : Number(e.target.value);
                setLibraryId(next);
                setSearchTerm('');
                setFolderFormOpen(false);
              }}
              aria-label="Spare library"
            >
              {libraries.length === 0 ? <option value="">No libraries</option> : null}
              {libraries.map((lib) => (
                <option key={lib.id} value={lib.id}>
                  {lib.name}
                </option>
              ))}
            </select>

            <div className="relative">
              <SearchIcon
                className={cn(
                  'absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none',
                  colors.textMuted,
                )}
              />
              <input
                type="search"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                placeholder="Filter folders…"
                disabled={libraryId == null}
                className={cn(
                  'w-full h-8 pl-8 pr-3 rounded-md border text-sm',
                  colors.border,
                  colors.surface,
                  colors.text,
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                  'disabled:opacity-50',
                )}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto dashboard-scrollbar py-1">
            {libraryId == null && (
              <p className={cn('px-3 py-6 text-sm text-center', colors.textMuted)}>
                Create a spare library first.
              </p>
            )}

            {libraryId != null && catsLoading && (
              <p className={cn('px-3 py-4 text-sm', colors.textMuted)}>Loading folders…</p>
            )}

            {libraryId != null && !catsLoading && categoryTree.length === 0 && (
              <p className={cn('px-3 py-6 text-sm text-center', colors.textMuted)}>
                No folders yet.
                {canEditFolders ? ' Use + to create one.' : ''}
              </p>
            )}

            {libraryId != null && !catsLoading && categoryTree.length > 0 && categoryRows.length === 0 && (
              <p className={cn('px-3 py-6 text-sm text-center', colors.textMuted)}>No folders match.</p>
            )}

            {libraryId != null &&
              !catsLoading &&
              categoryRows.map((row: CategoryTreeRow) => {
                const active = selectedCategoryId === row.id && !folderFormOpen;
                const isExpanded = row.hasChildren && !collapsedIds.has(row.id);
                const deletable = categoryCanDelete(row);
                const itemCount = countDirectItemsInCategory(libraryItems, row.id);
                const actionBtn = active
                  ? 'text-white/85 hover:text-white hover:bg-white/15'
                  : cn(colors.textMuted, colors.interactiveHover);
                return (
                  <div
                    key={row.id}
                    role="treeitem"
                    aria-selected={selectedCategoryId === row.id}
                    aria-expanded={row.hasChildren ? isExpanded : undefined}
                    onClick={() => selectCategory(row.id)}
                    className={cn(
                      'group/row flex items-center gap-1 h-9 cursor-pointer select-none transition-colors border-l-2',
                      active
                        ? cn(colors.primary, colors.primaryText, colors.primaryBorder)
                        : cn('border-transparent', colors.text, colors.interactiveHover),
                      !row.isActive && !active && 'opacity-60',
                    )}
                    style={{ paddingLeft: 8 + row.depth * 14, paddingRight: 6 }}
                    title={row.name}
                  >
                    <span className="w-5 shrink-0 flex items-center justify-center">
                      {row.hasChildren ? (
                        <button
                          type="button"
                          className={cn(
                            'h-6 w-6 inline-flex items-center justify-center rounded -m-0.5',
                            active ? 'text-white/85 hover:bg-white/15' : cn(colors.textMuted, colors.interactiveHover),
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCollapsed(row.id);
                          }}
                          title={isExpanded ? 'Collapse' : 'Expand'}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? (
                            <ChevronDownIcon className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRightIcon className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : (
                        <span className="w-6" aria-hidden />
                      )}
                    </span>

                    <span
                      className={cn(
                        'w-5 shrink-0 inline-flex items-center justify-center',
                        active ? 'text-white/90' : colors.textMuted,
                      )}
                      aria-hidden
                    >
                      <FolderIcon className="h-3.5 w-3.5" />
                    </span>

                    <span className={cn('min-w-0 flex-1 truncate text-sm', active && 'font-medium')}>
                      {row.name}
                    </span>

                    <span
                      className={cn(
                        'shrink-0 min-w-[1.25rem] text-right text-[11px] tabular-nums',
                        active ? 'text-white/70' : colors.textMuted,
                      )}
                      title={`${itemCount} direct item${itemCount === 1 ? '' : 's'}`}
                    >
                      {itemCount}
                    </span>

                    {(canEditFolders || canDeleteFolders) && (
                      <span
                        className={cn(
                          'shrink-0 flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100',
                          selectedCategoryId === row.id && 'opacity-100',
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canEditFolders && (
                          <>
                            <button
                              type="button"
                              className={cn('p-1 rounded', actionBtn)}
                              title="Edit folder"
                              aria-label="Edit folder"
                              onClick={() => openEditFolder(row)}
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className={cn('p-1 rounded', actionBtn)}
                              title="Add sub-folder"
                              aria-label="Add sub-folder"
                              onClick={() => openCreateFolder(row.id)}
                            >
                              <PlusIcon className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                        {canDeleteFolders && (
                          <button
                            type="button"
                            disabled={!deletable}
                            className={cn(
                              'p-1 rounded',
                              deletable
                                ? 'text-red-500 hover:bg-red-500/15'
                                : cn(colors.textMuted, 'opacity-40 cursor-not-allowed'),
                            )}
                            title={
                              deletable
                                ? 'Delete folder'
                                : 'Cannot delete — has sub-folders or spare items'
                            }
                            aria-label="Delete folder"
                            onClick={() => setDeleteFolderConfirm(row)}
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        </aside>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={panelWidth}
          aria-label="Resize categories panel"
          title="Drag to resize"
          onMouseDown={startResize}
          className={cn(
            'flex shrink-0 w-1.5 cursor-col-resize items-center justify-center group',
            'hover:bg-sky-500/20 active:bg-sky-500/30',
            'border-l',
            colors.border,
            panelResizing && 'bg-sky-500/30',
          )}
        >
          <div className={cn('w-0.5 h-12 rounded-full opacity-60 group-hover:opacity-100', colors.textMuted)} />
        </div>

        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          {folderFormOpen && libraryId != null ? (
            <SpareCategoryFormPanel
              libraryId={libraryId}
              parentId={folderFormEditItem ? (folderFormEditItem.pid ?? null) : folderFormParentId}
              parentLabel={folderFormParentLabel}
              editItem={folderFormEditItem}
              onCancel={closeFolderForm}
              onSaved={async (saved) => {
                await mutateCats();
                setSelectedCategoryId(saved.id);
                closeFolderForm();
                showSuccess(folderFormEditItem ? 'Folder saved' : 'Folder created');
              }}
            />
          ) : (
            <>
          <div
            className={cn(
              'flex-shrink-0 flex flex-col gap-2.5 px-4 py-3 border-b',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                {breadcrumb.length > 0 ? (
                  <nav className="flex flex-wrap items-center gap-1 min-w-0 text-sm" aria-label="Category path">
                    {breadcrumb.map((crumb, index) => {
                      const isLast = index === breadcrumb.length - 1;
                      return (
                        <React.Fragment key={crumb.id}>
                          {index > 0 && (
                            <span className={cn('shrink-0', colors.textMuted)} aria-hidden>
                              /
                            </span>
                          )}
                          {isLast ? (
                            <span className={cn('font-semibold truncate', colors.text)} title={crumb.name}>
                              {crumb.name}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => selectCategory(crumb.id)}
                              className={cn('truncate hover:underline', colors.textMuted)}
                              title={crumb.name}
                            >
                              {crumb.name}
                            </button>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </nav>
                ) : (
                  <p className={cn('text-sm font-semibold', colors.text)}>Select a folder</p>
                )}
                <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                  {selectedCategory
                    ? `${linkedItems.length} item${linkedItems.length === 1 ? '' : 's'}${
                        includeSubcats ? ' (including subfolders)' : ''
                      }`
                    : 'Choose a folder on the left'}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <ListRefreshHint
                  colors={colors}
                  isRefreshing={isRefreshing}
                  lastRefreshedAt={lastRefreshedAt}
                  onRefresh={handleRefresh}
                />
                {canEdit && selectedCategory && (
                  <button
                    type="button"
                    onClick={() => navigate(newItemHref)}
                    className={cn(
                      'inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors hover:opacity-90',
                      colors.primary,
                      colors.primaryText,
                    )}
                    title="New spare item"
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                    New item
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[12rem] flex-1">
                <SearchIcon
                  className={cn(
                    'absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none',
                    colors.textMuted,
                  )}
                />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search name, vendor code, drawing, serial…"
                  disabled={!selectedCategory}
                  className={cn(
                    'w-full h-8 pl-8 pr-3 rounded-md border text-sm',
                    colors.border,
                    colors.surface,
                    colors.text,
                    'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                    'disabled:opacity-50',
                  )}
                />
              </div>
              <div
                className={cn('inline-flex rounded-md border p-0.5 text-xs', colors.border, colors.surface)}
                role="group"
                aria-label="Folder scope"
              >
                <button
                  type="button"
                  onClick={() => setIncludeSubcats(false)}
                  className={cn(
                    'h-7 px-2.5 rounded transition-colors',
                    !includeSubcats
                      ? cn(colors.primary, colors.primaryText)
                      : cn(colors.textMuted, colors.interactiveHover),
                  )}
                >
                  This folder
                </button>
                <button
                  type="button"
                  onClick={() => setIncludeSubcats(true)}
                  className={cn(
                    'h-7 px-2.5 rounded transition-colors',
                    includeSubcats
                      ? cn(colors.primary, colors.primaryText)
                      : cn(colors.textMuted, colors.interactiveHover),
                  )}
                >
                  + Subfolders
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0">
            <SpareItemsTable
              items={linkedItems}
              loading={isLoading}
              canEdit={canEdit}
              canDelete={canDelete}
              onView={(row) => navigate(moduleConfig.module.route.detail(row.id))}
              onEdit={(row) => navigate(moduleConfig.module.route.edit(row.id))}
              onDelete={(row) => setDeleteConfirmId(row.id)}
              emptyMessage={
                !selectedCategory
                  ? 'Select a folder to view items.'
                  : searchTerm.trim()
                    ? moduleConfig.emptyStates.noSearchResults
                    : includeSubcats
                      ? 'No spare items in this folder or its subfolders.'
                      : 'No direct items here. Switch to “+ Subfolders” or pick a child folder.'
              }
            />
          </div>
            </>
          )}
        </div>
      </div>

      {deleteConfirmId != null && (
        <ConfirmationDialog
          isOpen
          onClose={() => setDeleteConfirmId(null)}
          onConfirm={() => handleDelete(deleteConfirmId)}
          title={moduleConfig.delete.title}
          message={moduleConfig.delete.message}
          confirmText={moduleConfig.delete.confirmText}
          cancelText={moduleConfig.delete.cancelText}
          confirmVariant="danger"
        />
      )}

      {deleteFolderConfirm != null && (
        <ConfirmationDialog
          isOpen
          onClose={() => setDeleteFolderConfirm(null)}
          onConfirm={() => handleDeleteFolder(deleteFolderConfirm.id)}
          title={categoriesModuleConfig.delete.title}
          message={
            deleteFolderConfirm.hasChildren || !categoryCanDelete(deleteFolderConfirm)
              ? 'This folder still has sub-folders or spare items. Remove them first.'
              : `Delete folder “${deleteFolderConfirm.name}”?`
          }
          confirmText={categoriesModuleConfig.delete.confirmText}
          cancelText={categoriesModuleConfig.delete.cancelText}
          confirmVariant="danger"
        />
      )}
    </div>
  );
};

export default List;
